import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@renderer/chat/providers'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'

import { STAGED_ACTIONS_QUERY_KEY } from './utils'
import { AnswerableEndpointError } from './answerable-dismissal'
import type { AnswerableProgress } from '@renderer/chat/integrations/answerable'
import type { StagedAction } from './types'

export function useStagedActionEndpoints() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  /**
   * Records the user's answers (or their dismissal) for a pending ask and
   * resolves it, which fires the hook that wakes the run that asked. The two
   * tools post to different paths but are otherwise identical, hence `endpoint`.
   *
   * Returns the resolved action when the body parses; the answer is already
   * persisted either way, so a failure to read it back is not an error.
   */
  const resolveAnswerableStagedAction = useCallback(
    async (id: string, endpoint: 'feedback' | 'answer', answer: AnswerableProgress): Promise<StagedAction | null> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}/${endpoint}`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(answer),
      })
      if (!response.ok) {
        throw new AnswerableEndpointError(response.status, `Failed to submit ${endpoint} (status ${response.status})`)
      }
      try {
        return ((await response.json()) as { data?: StagedAction }).data ?? null
      } catch {
        return null
      }
    },
    [getAuthToken],
  )

  const deleteStagedActionBatch = useCallback(
    async (ids: Array<string>): Promise<void> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/staged-actions'), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ids),
      })
      if (!response.ok) {
        throw new Error('Failed to bulk delete staged actions')
      }
    },
    [getAuthToken],
  )

  const archiveStagedActionBatch = useCallback(
    async (ids: Array<string>): Promise<void> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/staged-actions/archive'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ids),
      })
      if (!response.ok) {
        throw new Error('Failed to bulk archive staged actions')
      }
    },
    [getAuthToken],
  )

  const unarchiveStagedActionBatch = useCallback(
    async (ids: Array<string>): Promise<void> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/staged-actions/unarchive'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ids),
      })
      if (!response.ok) {
        throw new Error('Failed to bulk unarchive staged actions')
      }
    },
    [getAuthToken],
  )

  const bulkPatchMetadata = useCallback(
    async (updates: Array<{ id: string; metadata: Record<string, string> }>): Promise<void> => {
      if (updates.length === 0) return
      try {
        const token = await getAuthToken()
        const response = await fetch(getChatApiRoute('api/v1/staged-actions'), {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        })
        if (!response.ok) {
          throw new Error('Failed to bulk update staged action metadata')
        }
      } catch (error) {
        console.error('[bulkPatchMetadata] failed:', error)
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      }
    },
    [getAuthToken, queryClient],
  )

  return {
    resolveAnswerableStagedAction,
    deleteStagedActionBatch,
    archiveStagedActionBatch,
    unarchiveStagedActionBatch,
    bulkPatchMetadata,
  }
}
